import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";

function containingScroller(node: HTMLElement): HTMLElement | null {
  for (let parent = node.parentElement; parent; parent = parent.parentElement) {
    if (/^(auto|scroll)$/.test(getComputedStyle(parent).overflowY)) return parent;
  }
  return null;
}

/** The app's one "more" control, whether a list continues by itself or on request. */
export function LoadMoreButton({
  label = "Load more", isLoading = false, disabled = false, onClick,
}: {
  label?: string;
  isLoading?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return <div className="flex w-full justify-center py-4">
    <Button variant="ghost" size="control" disabled={disabled || isLoading} onClick={onClick}>
      <span className={isLoading ? "loading-dots" : undefined}>{isLoading ? "Loading more" : label}</span>
    </Button>
  </div>;
}

/** All scrolling galleries continue against their actual scroll surface. The
 * next page starts within one viewport of the end. Short pages can fill that
 * runway one request at a time; an unchanged cursor stops automatic retries.
 * The button also allows keyboard continuation and retry after a page adds no items. */
export function InfiniteScrollSentinel({
  enabled = true, hasMore, isLoading, onLoadMore, root, cursor,
}: {
  enabled?: boolean;
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => unknown;
  root?: HTMLElement | null;
  cursor?: string | number;
}) {
  const sentinel = useRef<HTMLDivElement>(null);
  const attempted = useRef<{ cursor: string | number | undefined } | null>(null);
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !enabled || !hasMore || isLoading) return;
    const scroller = root === undefined ? containingScroller(node) : root;
    let observer: IntersectionObserver | undefined;
    let observedHeight = -1;
    const updateObserver = () => {
      const height = Math.round(scroller?.clientHeight ?? document.documentElement.clientHeight);
      if (height === observedHeight) return;
      observedHeight = height;
      observer?.disconnect();
      if (height <= 0) return;
      observer = new IntersectionObserver(entries => {
        if (!entries[0]?.isIntersecting || (attempted.current && attempted.current.cursor === cursor)) return;
        attempted.current = { cursor };
        void onLoadMore();
      }, { root: scroller, rootMargin: `0px 0px ${height}px 0px` });
      observer.observe(node);
    };
    const resize = new ResizeObserver(updateObserver);
    resize.observe(scroller ?? document.documentElement);
    window.addEventListener("resize", updateObserver);
    updateObserver();
    return () => {
      observer?.disconnect();
      resize.disconnect();
      window.removeEventListener("resize", updateObserver);
    };
  }, [enabled, hasMore, isLoading, onLoadMore, root, cursor]);

  if (!hasMore && !isLoading) return null;
  return <div ref={sentinel}>
    <LoadMoreButton isLoading={isLoading} disabled={!enabled}
      onClick={() => { attempted.current = { cursor }; void onLoadMore(); }} />
  </div>;
}
